// Client-side Web Push subscription helpers.
//
// Flow (one-time per device):
//   1. registerServiceWorker() — registers /sw.js, called on app load.
//   2. enablePushNotifications() — asks for permission, subscribes via
//      PushManager, POSTs the subscription to our backend.
//   3. Backend keeps the subscription, sends pushes whenever a notification
//      is created targeting that user/role.
//
// A "subscription" is just an opaque endpoint URL + two encryption keys.
// It's tied to (browser × VAPID-public-key × user permission), so each
// device the user logs into needs to subscribe once.

import { api } from "../api";

// Stash the VAPID key in memory so we don't refetch on every retry.
const _state = { vapidKey: null };

// PushManager subscribe expects the VAPID public key as a Uint8Array.
// Browsers transmit it as base64url; we have to decode.
function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
}

export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export function getNotificationPermission() {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission; // 'default' | 'granted' | 'denied'
}

// Register the service worker. Idempotent — safe to call on every app load.
export async function registerServiceWorker() {
    if (!("serviceWorker" in navigator)) return null;
    try {
        // /sw.js is served from the public/ folder so it sits at the root,
        // which gives the SW the widest possible scope (the whole origin).
        const reg = await navigator.serviceWorker.register("/sw.js");
        return reg;
    } catch (e) {
        console.warn("[push] SW register failed:", e && e.message);
        return null;
    }
}

async function fetchVapidKey() {
    if (_state.vapidKey) return _state.vapidKey;
    try {
        const data = await api.getVapidKey();
        if (data && data.key) {
            _state.vapidKey = data.key;
            return data.key;
        }
    } catch (e) { /* swallow */ }
    return null;
}

// Ask permission + subscribe + persist to backend. Idempotent.
// Returns { ok: boolean, reason?: string }.
export async function enablePushNotifications() {
    if (!isPushSupported()) return { ok: false, reason: "unsupported" };

    // iOS Safari only allows push on installed PWAs ("Add to Home Screen").
    // We can't detect that perfectly, but if Notification.requestPermission
    // is missing or always returns 'denied', it usually means standalone-only.

    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: perm };

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const key = await fetchVapidKey();
        if (!key) return { ok: false, reason: "no-vapid-key" };
        try {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true, // browsers require this for Web Push
                applicationServerKey: urlBase64ToUint8Array(key),
            });
        } catch (e) {
            console.error("[push] subscribe() failed:", e && e.message);
            return { ok: false, reason: "subscribe-failed" };
        }
    }

    try {
        // sub.toJSON() shape: { endpoint, keys: { p256dh, auth } } — exactly
        // what /api/push/subscribe expects.
        await api.subscribePush(sub.toJSON());
    } catch (e) {
        console.error("[push] backend register failed:", e && e.message);
        return { ok: false, reason: "backend-failed" };
    }

    return { ok: true };
}

// Remove the subscription locally + on the backend. Used by the toggle-off
// path in Settings.
export async function disablePushNotifications() {
    if (!isPushSupported()) return { ok: true };
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            try { await api.unsubscribePush(sub.endpoint); } catch (_) {}
            try { await sub.unsubscribe(); } catch (_) {}
        }
        return { ok: true };
    } catch (e) {
        return { ok: false, reason: e && e.message };
    }
}

// Check whether the *current* device has an active subscription registered
// on the server. Used to drive the toggle UI's initial state.
export async function isPushEnabledHere() {
    if (!isPushSupported()) return false;
    if (Notification.permission !== "granted") return false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        return !!sub;
    } catch (_) {
        return false;
    }
}
